import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';

const faqs = [
  {
    category: 'About the Show',
    questions: [
      {
        q: 'What is Mental Hop Scotch?',
        a: 'Mental Hop Scotch is a 90-minute live mentalism experience starring Simon Nomis. It is an interactive show where Simon reads thoughts, predicts choices, and reveals hidden connections between audience members — all live, in real time, with no editing or second takes.',
      },
      {
        q: 'Is Mental Hop Scotch a magic show?',
        a: 'No. Mental Hop Scotch is a mentalism show, not a traditional magic show. There are no rabbits, cards, or illusions with props. Instead, the show is built entirely on the audience — your thoughts, memories, and choices become the performance.',
      },
      {
        q: 'Who is Simon Nomis?',
        a: 'Simon Nomis is a mentalist and behavioral expert based in Orlando, Florida. His work is rooted in psychology, observation, and the science of how humans think, choose, and connect. He does not claim supernatural abilities.',
      },
      {
        q: 'How long is the show?',
        a: 'Mental Hop Scotch runs for 90 minutes with no intermission. The pacing is brisk and engaging throughout.',
      },
      {
        q: 'What kind of things happen during the show?',
        a: 'Simon reveals names of people audience members are thinking about, predicts choices before they are made, demonstrates impossible connections between strangers, and builds toward a finale that ties every moment of the evening together. The show features audience participation throughout.',
      },
    ],
  },
  {
    category: 'Tickets & Pricing',
    questions: [
      {
        q: 'How much do tickets cost for Mental Hop Scotch?',
        a: 'Tickets start at $30 for Standard Experience, $35 for Premium Experience, and $45 for VIP Experience. VIP includes front-row seating and additional perks.',
      },
      {
        q: 'Where can I buy tickets?',
        a: 'Tickets are available at simonnomislive.com. You can also find showtime information and VIP package details on the website.',
      },
      {
        q: 'Are there group discounts available?',
        a: 'For group bookings and special event inquiries, please contact us through the website at simonnomislive.com.',
      },
    ],
  },
  {
    category: 'Venue & Location',
    questions: [
      {
        q: 'Where is Mental Hop Scotch performed?',
        a: 'Mental Hop Scotch performs at the Goldman Theater at the Orlando Shakespeare Theater (John and Rita Lowndes Shakespeare Center) in Orlando, Florida.',
      },
      {
        q: 'Is there parking available?',
        a: 'Yes, free parking is available at the venue.',
      },
      {
        q: 'What is the Goldman Theater like?',
        a: 'The Goldman Theater is an intimate, acoustically rich performance space — one of Central Florida\'s premier theatrical venues. The close seating makes it the perfect setting for mentalism, where you can feel every gasp and moment of collective wonder.',
      },
    ],
  },
  {
    category: 'Audience & Participation',
    questions: [
      {
        q: 'Will I be forced to go on stage?',
        a: 'No. While audience members are selected at random to participate, no one is ever forced on stage or put in an uncomfortable position. Simon reads the room brilliantly and makes participation feel like a prize, not a pressure.',
      },
      {
        q: 'What if I\'m shy or don\'t want to participate?',
        a: 'You will still have an incredible experience from your seat. The vast majority of the audience enjoys the show without going on stage. Every seat is part of the experience.',
      },
      {
        q: 'Is Mental Hop Scotch appropriate for children?',
        a: 'The show is recommended for ages 16 and up. It is clean and joyful, but the emotional and psychological depth tends to resonate most powerfully with teens and adults.',
      },
      {
        q: 'Is this a good date night experience?',
        a: 'Absolutely. Mental Hop Scotch is widely considered one of the best date night experiences in Orlando. The interactive nature gives couples a shared experience they\'ll talk about for weeks, and the intimate Goldman Theater setting makes the evening feel special.',
      },
      {
        q: 'I\'m a skeptic. Will I enjoy the show?',
        a: 'Skeptics often have the biggest reactions. Simon welcomes doubters — the more certain you are that you can\'t be surprised, the more spectacular it is when it happens. Bring your analytical mind. It won\'t help you, and that\'s the fun of it.',
      },
    ],
  },
  {
    category: 'Before You Go',
    questions: [
      {
        q: 'What time should I arrive?',
        a: 'We recommend arriving 15-20 minutes early to settle in and soak up the pre-show atmosphere. Some things on stage are worth noticing before the show begins.',
      },
      {
        q: 'Can I use my phone during the show?',
        a: 'There will be specific moments where phones are welcome, but for the best experience, we recommend putting your phone away and being fully present. This is a live, in-the-moment experience that deserves your full attention.',
      },
      {
        q: 'Is there food or drinks available?',
        a: 'Check with the venue for current concession offerings. We recommend having dinner before the show and saving drinks for afterward — the post-show conversation is half the fun.',
      },
    ],
  },
];

const allFaqsFlat = faqs.flatMap(cat => cat.questions);

const FAQPage = () => {
  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>FAQ - Mental Hop Scotch | Simon Nomis Live Orlando</title>
        <meta name="description" content="Frequently asked questions about Mental Hop Scotch, the live mentalism show starring Simon Nomis at the Goldman Theater in Orlando, Florida. Tickets, showtimes, venue info, and more." />
        <meta name="keywords" content="Mental Hop Scotch FAQ, Simon Nomis questions, Orlando mentalism show, Goldman Theater, tickets, parking, age requirement" />
        <link rel="canonical" href="https://www.simonnomislive.com/faq" />
        <meta property="og:title" content="FAQ - Mental Hop Scotch | Simon Nomis Live" />
        <meta property="og:description" content="Everything you need to know about Mental Hop Scotch — tickets, venue, participation, and what to expect." />
        <meta property="og:url" content="https://www.simonnomislive.com/faq" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": allFaqsFlat.map(faq => ({
            "@type": "Question",
            "name": faq.q,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.a,
            },
          })),
        })}</script>
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-black via-purple-900/20 to-black">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6" data-testid="faq-heading">
              Frequently Asked <span className="text-purple-400">Questions</span>
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Everything you need to know about Mental Hop Scotch, tickets, the venue, and what to expect on show night.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto space-y-12">
            {faqs.map((category) => (
              <div key={category.category}>
                <h2 className="text-2xl font-bold text-purple-400 mb-6 pb-2 border-b border-gray-800">
                  {category.category}
                </h2>
                <div className="space-y-6">
                  {category.questions.map((faq, i) => (
                    <div key={i} className="bg-gray-900/40 border border-gray-800 rounded-lg p-6" data-testid={`faq-item-${i}`}>
                      <h3 className="text-lg font-semibold text-white mb-3">{faq.q}</h3>
                      <p className="text-gray-300 leading-relaxed">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-purple-900/20 via-black to-purple-900/20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to <span className="text-purple-400">Experience It</span>?
            </h2>
            <p className="text-gray-300 mb-8 text-lg">
              Stop wondering and come see it for yourself. Book your tickets for Mental Hop Scotch today.
            </p>
            <Link
              to="/#tickets"
              className="inline-block font-bold text-xl text-white rounded-full px-10 py-4 transition-all duration-300 hover:scale-105 hover:opacity-90"
              style={{ backgroundColor: 'rgb(181, 36, 189)' }}
              data-testid="faq-cta-button"
            >
              Get Tickets
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;
